import { __assign } from "tslib";
import { useEffect, useState } from 'react';
/* eslint-disable no-restricted-imports, import/named */
import { useController, useFormContext, } from 'react-hook-form';
import { useTemporaryBrowserData } from '../components/TemporaryBrowserData';
var useFormInput = function (_a) {
    var _b, _c;
    var initialValue = _a.initialValue, label = _a.label, name = _a.name, rules = _a.rules, _d = _a.shouldValidateOnChange, shouldValidateOnChange = _d === void 0 ? false : _d;
    var _e = useFormContext(), control = _e.control, _f = _e.value, contextValue = _f === void 0 ? {} : _f;
    var setValue = control.setValue;
    var initialValues = contextValue.initialValues;
    /**
     * An edge-case scenario occurs when manually clearing TemporaryBrowserData that is caused
     * by sequential render loops.  TemporaryBrowserDataContext uses the same approach as below
     * to keep track of the last time data was manually cleared in order to force components
     * built on top of difficult to use 3rd party libraries (e.g. RichTextField, AdvancedSelect)
     * to reset their value.  This update happens quickly, however using setValue from react-hook-form
     * takes an additional render cycle to update field.value, meaning the datetime returned by
     * useTemporaryBrowserData's dataLastManuallyCleared field will not correctly reset the values
     * of components like RichTextField.
     *
     * In order to make everything work nicely, we need to watch dataLastManuallyCleared with a
     * useEffect and generate a NEW datetime and store it in temporaryDataLastCleared in order
     * to ensure the correct value is used when downstream components run their forced updates
     */
    var _g = useState(null), temporaryDataLastCleared = _g[0], setTemporaryDataLastCleared = _g[1];
    /**
     * Try to retrieve an initial value from TemporaryBrowserData.  If it exists, use
     * the value stored in local storage for the initial value
     */
    var _h = useTemporaryBrowserData(), data = _h.data, dataLastManuallyCleared = _h.dataLastManuallyCleared, getLatestData = _h.getLatestData, updateData = _h.updateData;
    var temporaryDataForInput = data === null || data === void 0 ? void 0 : data.content[name];
    /**
     * When the user clicks the Discard Changes button provided by TemporaryBrowserDataStatus
     * the dataLastManuallyCleared value will change to the value of Date.now().  When this happens
     * we need to manually reset the Form & all of it's components to their initial state
     */
    useEffect(function () {
        if (dataLastManuallyCleared) {
            // If the initialValue is not available when the form initializes,
            // the field needs to be reset when it is passed in.
            var initialInputValue = (initialValues === null || initialValues === void 0 ? void 0 : initialValues[name]) || initialValue || null;
            setValue(name, initialInputValue, {
                // Setting shouldValidate to false to prevent validation errors from appearing when TemporaryBrowserData is cleared on form submit
                // https://thrivetrm.atlassian.net/browse/THRIVE-12497
                shouldValidate: false,
                shouldDirty: false
            });
            setTemporaryDataLastCleared(Date.now());
        }
        /**
         * Disabling exhaustive-deps for this line because we only want this code to run
         * when the user manually clears temporary data and an updated datetime in ms is
         * returned from useTemporaryBrowserData
         *
         * Putting this logic into a useEffect with limited deps ensures all Form data
         * is reset correctly
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataLastManuallyCleared]);
    /**
     * There is an edge case scenario where a Form input has an initial value provided
     * AND draft data exists but is falsy.  In these scenarios we want to display the
     * falsy value from draft storage and NOT the initial value
     */
    var falsyValues = ['', false, null, 0];
    var isValidTemporaryData = temporaryDataForInput || falsyValues.includes(temporaryDataForInput);
    useEffect(function () {
        // If the initialValue is not available when the form initializes,
        // the field needs to be reset when it is passed in.
        var initialInputValue = (initialValues && initialValues[name]) || initialValue;
        if (isValidTemporaryData) {
            setValue(name, temporaryDataForInput, {
                shouldValidate: true,
                shouldDirty: false
            });
        }
        else {
            setValue(name, initialInputValue, {
                shouldValidate: true,
                shouldDirty: false
            });
        }
        /**
         * Disabling exhaustive-deps for this line because we only want this code to run
         * while a potentially async request is being made to fetch initial values for a form.
         *
         * TemporaryBrowserData is synchronous and will either have a value or not.  We DO NOT
         * want to run this code every time temporaryDataForInput or updateData changes
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, initialValue, initialValues, name, setValue]);
    // Initially the Form component received initial values via the initialValues prop
    // on the form component.  The below allows for the old implementation to still work.
    var getDefaultValue = function () {
        if (isValidTemporaryData) {
            return temporaryDataForInput;
        }
        else if (initialValues === null || initialValues === void 0 ? void 0 : initialValues[name]) {
            return initialValues[name];
        }
        else {
            return initialValue;
        }
    };
    var defaultValue = getDefaultValue();
    var _j = useController({
        name: name,
        control: control,
        rules: rules,
        defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : null
    }), field = _j.field, meta = _j.meta;
    var errorMessage = (_b = meta === null || meta === void 0 ? void 0 : meta.invalid) === null || _b === void 0 ? void 0 : _b.message;
    var isRequired = ((_c = rules === null || rules === void 0 ? void 0 : rules.required) === null || _c === void 0 ? void 0 : _c.value) === true;
    var handleChange = function (newValue) {
        var _a;
        // The default onChange returned by react-hook-form is equivalent to:
        // (newValue) => setValue(name, newValue, { shouldDirty: true})
        // The default onBlur returned by react-hook-form is equivalent to:
        // (newValue) => setValue(name, newValue, { shouldDirty: true, shouldValidate: true})
        // The custom handleChange function triggers form validation onChange when:
        //   - The input is in an error state
        //   - The input is required
        //   - the input has the shouldValidateOnChange prop
        // For all other cases, form validation is triggered by the blur event.
        if (errorMessage || shouldValidateOnChange || isRequired) {
            setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
        }
        else {
            setValue(name, newValue, { shouldDirty: true });
        }
        if (typeof data !== 'undefined' && updateData) {
            var latestData = getLatestData();
            updateData(__assign(__assign({}, latestData === null || latestData === void 0 ? void 0 : latestData.content), (_a = {}, _a[name] = newValue, _a)));
        }
    };
    return {
        dataLastManuallyCleared: temporaryDataLastCleared,
        errorMessage: errorMessage,
        formattedLabel: isRequired && label ? "* ".concat(label) : label,
        isRequired: isRequired,
        onBlur: errorMessage ? function () { } : field.onBlur,
        onChange: handleChange,
        value: field === null || field === void 0 ? void 0 : field.value
    };
};
export default useFormInput;
//# sourceMappingURL=useFormInput.js.map